import { useCallback } from 'react';
import { getDataTableColumns } from '../cols-definition';
import { SearchFormValues } from '../schema';
import { useDynamicSearch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

interface UseSearchTableProps {
  searchData: (filters: Record<string, MuaHangRecord>, page?: number, searchPageSize?: number) => Promise<void>;
  searchParams: SearchFormValues;
  setSearchParams: (params: SearchFormValues) => void;
  handleViewClick: (obj: MuaHangRecord) => void;
  sumTotal: number | null;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
}

export const useSearchTable = ({
  searchData,
  searchParams,
  setSearchParams,
  handleViewClick,
  sumTotal,
  isFiltered,
  handlePageChange
}: UseSearchTableProps) => {
  const onSearch = useCallback(
    async (params: MuaHangRecord) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (formData: MuaHangRecord, onSearchFn: (params: MuaHangRecord) => Promise<void>) => {
      const { dynamic_filters, ...searchParamsOnly } = formData;
      setSearchParams(searchParamsOnly as SearchFormValues);
      await onSearchFn(formData);
    },
    [setSearchParams]
  );

  const getColumns = useCallback(() => {
    const cols = getDataTableColumns(handleViewClick, sumTotal);
    return cols.map(col => ({
      field: col.field,
      type: col.type
    }));
  }, [handleViewClick, sumTotal]);

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: searchParams,
    onInitialSearch,
    getColumns,
    endpoint: `${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/filter`,
    pageSize: 20,
    sumField: 't_tt'
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
      } else if (isFiltered) {
        await searchData(searchParams, page);
      } else {
        await handlePageChange(page);
      }
    },
    [hasFilterParams, handleSearchPageChange, isFiltered, searchParams, searchData, handlePageChange]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
